/**
 * Der Hinweis in der aufgeklappten Zeile eines Projekts, solange es dort keine Unterzeilen gibt:
 * Es läuft keine Genehmigung, das Recht fehlt, die Anträge laden noch oder es gibt keinen. Gibt
 * es Anträge, stehen sie als Unterzeilen in den Spalten der Tabelle (`usePermitSubRows`), und
 * die Seite zeigt diesen Hinweis nicht.
 *
 * Termine stehen nicht hier – die Kachel „Termine“ zeigt im Fokus die des Projekts.
 */
import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router";
import { PermitSummary } from "../../../../types";
import Button from "../../../common/inputs/Button";
import { colors } from "../../../../styles/theme";

interface ProjectFocusDetailProps {
  projectId: string;
  /** Ob die Genehmigung gerade läuft – nur dann gibt es hier etwas zu tun */
  permitsRunning: boolean;
  /** Ob man die Anträge des Projekts sehen darf */
  canSeePermits: boolean;
  /** Die Anträge des Projekts; `null`, solange sie laden */
  summary: PermitSummary | null;
  failed: boolean;
}

const ProjectFocusDetail: React.FC<ProjectFocusDetailProps> = ({
  projectId,
  permitsRunning,
  canSeePermits,
  summary,
  failed,
}) => {
  const navigate = useNavigate();

  const hint = (text: string) => (
    <Typography variant="body2" sx={{ color: colors.text.secondary }}>
      {text}
    </Typography>
  );

  if (!permitsRunning) return hint("Gerade läuft keine Genehmigung");
  if (!canSeePermits) return hint("Keine Berechtigung für die Genehmigung");
  if (failed) return hint("Die Anträge konnten nicht geladen werden");
  if (!summary) return <CircularProgress size={20} />;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      {hint("Noch kein Antrag")}
      <Button
        variant="secondary"
        size="small"
        onClick={() => navigate(`/project/${projectId}/permits`)}
      >
        Antrag anlegen
      </Button>
    </Box>
  );
};

export default ProjectFocusDetail;
